export interface KmlPlacemark {
  name: string;
  lat: number | null;
  lng: number | null;
  mediaUrls: string[];
}

export function parseKml(xmlString: string): KmlPlacemark[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "application/xml");
  const parseError = doc.getElementsByTagName("parsererror")[0];
  if (parseError) throw new Error("Invalid KML: " + (parseError.textContent || "").slice(0, 200));

  const placemarks = Array.from(doc.getElementsByTagName("Placemark"));
  const out: KmlPlacemark[] = [];
  for (const pm of placemarks) {
    const name = (pm.getElementsByTagName("name")[0]?.textContent || "").trim();
    const coordsText = pm.getElementsByTagName("coordinates")[0]?.textContent || "";
    const coords = coordsText.trim().split(/\s+/)[0] || "";
    const [lonStr, latStr] = coords.split(",");
    const lng = parseFloat(lonStr);
    const lat = parseFloat(latStr);

    const mediaUrls: string[] = [];
    const dataNodes = pm.getElementsByTagName("Data");
    for (const d of Array.from(dataNodes)) {
      if (d.getAttribute("name") === "gx_media_links") {
        const v = d.getElementsByTagName("value")[0]?.textContent || "";
        v.trim().split(/\s+/).filter(Boolean).forEach((u) => mediaUrls.push(u));
      }
    }

    out.push({
      name,
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
      mediaUrls,
    });
  }
  return out;
}

export function normalizeName(s: unknown): string {
  if (s == null) return "";
  return String(s).normalize("NFKC").replace(/\s+/g, "").toLowerCase();
}

export function parsePlacemarkName(name: string): { deptHint: string; building: string; floor: string } {
  const parts = String(name || "")
    .split(/[\s　]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return { deptHint: "", building: "", floor: "" };
  if (parts.length === 1) return { deptHint: "", building: "", floor: parts[0] };
  if (parts.length === 2) return { deptHint: "", building: parts[0], floor: parts[1] };
  const floor = parts[parts.length - 1];
  const building = parts[parts.length - 2];
  const deptHint = parts.slice(0, parts.length - 2).join("");
  return { deptHint, building, floor };
}

function bldgFloorKey(building: unknown, floor: unknown): string {
  return normalizeName(`${building}|${floor}`);
}

interface RowEntry { rowIdx: number; dept: string; building?: string }
interface MatchCols { dept: number; building: number; floor: number }

export function matchPlacemarksToRows(
  rows: unknown[][],
  placemarks: KmlPlacemark[],
  cols: MatchCols
): { matches: Map<number, number>; unmatched: number[] } {
  const { dept: deptCol, building: buildingCol, floor: floorCol } = cols;
  const buckets = new Map<string, RowEntry[]>();
  const deptBuckets = new Map<string, Array<RowEntry & { building: string }>>();
  rows.forEach((r, i) => {
    const entry: RowEntry = { rowIdx: i, dept: String(r[deptCol] || "") };
    const bldgKey = bldgFloorKey(r[buildingCol], r[floorCol]);
    if (bldgKey && bldgKey !== "|" && normalizeName(r[buildingCol])) {
      if (!buckets.has(bldgKey)) buckets.set(bldgKey, []);
      buckets.get(bldgKey)!.push(entry);
    }
    const deptKey = bldgFloorKey(r[deptCol], r[floorCol]);
    if (deptKey && deptKey !== "|" && normalizeName(r[deptCol])) {
      if (!deptBuckets.has(deptKey)) deptBuckets.set(deptKey, []);
      deptBuckets.get(deptKey)!.push({ ...entry, building: String(r[buildingCol] || "") });
    }
  });

  const matches = new Map<number, number>();
  const unmatched: number[] = [];
  const fallbackByKey = new Map<string, number>();

  placemarks.forEach((pm, pmIdx) => {
    const { deptHint, building, floor } = parsePlacemarkName(pm.name);
    const key = bldgFloorKey(building, floor);
    let bucket: RowEntry[] | undefined = buckets.get(key);
    let activeKey = key;
    if (!bucket || bucket.length === 0) {
      const normBuilding = normalizeName(building);
      const candidates = [
        { key: bldgFloorKey(building, floor), enforceBuilding: !!deptHint },
        { key: bldgFloorKey(deptHint, floor), enforceBuilding: true },
      ];
      for (const { key: candidateKey, enforceBuilding } of candidates) {
        const alt = deptBuckets.get(candidateKey);
        if (!alt || alt.length === 0) continue;
        const filtered = enforceBuilding
          ? alt.filter((c) => {
              const candB = normalizeName(c.building);
              return !candB || !normBuilding || candB === normBuilding;
            })
          : alt;
        if (filtered.length > 0) {
          bucket = filtered;
          activeKey = candidateKey;
          break;
        }
      }
    }
    if (!bucket || bucket.length === 0) {
      if (fallbackByKey.has(key)) {
        matches.set(pmIdx, fallbackByKey.get(key)!);
      } else {
        unmatched.push(pmIdx);
      }
      return;
    }
    let pickIdx = -1;
    if (deptHint) {
      const normHint = normalizeName(deptHint);
      let bestScore = 0;
      bucket.forEach((cand, i) => {
        const normDept = normalizeName(cand.dept);
        if (!normDept) return;
        if (normDept.includes(normHint) || normHint.includes(normDept)) {
          const score = Math.min(normDept.length, normHint.length);
          if (score > bestScore) {
            bestScore = score;
            pickIdx = i;
          }
        }
      });
      if (pickIdx < 0) {
        if (fallbackByKey.has(key)) {
          matches.set(pmIdx, fallbackByKey.get(key)!);
        } else {
          unmatched.push(pmIdx);
        }
        return;
      }
    } else {
      pickIdx = 0;
    }
    const chosen = bucket.splice(pickIdx, 1)[0];
    matches.set(pmIdx, chosen.rowIdx);
    Array.from(buckets.values()).forEach((b) => {
      const idx = b.findIndex((e: RowEntry) => e.rowIdx === chosen.rowIdx);
      if (idx >= 0) b.splice(idx, 1);
    });
    Array.from(deptBuckets.values()).forEach((b) => {
      const idx = b.findIndex((e: RowEntry) => e.rowIdx === chosen.rowIdx);
      if (idx >= 0) b.splice(idx, 1);
    });
    if (!fallbackByKey.has(key)) fallbackByKey.set(key, chosen.rowIdx);
    if (activeKey !== key && !fallbackByKey.has(activeKey))
      fallbackByKey.set(activeKey, chosen.rowIdx);
  });

  return { matches, unmatched };
}
